import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import LandedCostCalculator from '../components/LandedCostCalculator';
import AnalysisComments from '../components/AnalysisComments';
import { 
  ArrowLeft, 
  Download, 
  TrendingUp, 
  Package, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Info,
  DollarSign,
  BarChart3,
  PieChart,
  Sparkles,
  Globe,
  Clock,
  Star,
  ArrowRight,
  ArrowRightLeft,
  Copy,
  Check,
  FileText,
  Zap,
  Target,
  ClipboardList,
  Share2,
  Link as LinkIcon,
  FileDown,
  Calculator,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Brain,
  Users,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { AIFeedback } from './analysis/AIFeedback';
import { apiGet, apiPost, apiFetch } from '../lib/apiClient';


const AnalysisResults = ({ user }) => {
  const { analysisId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSku, setExpandedSku] = useState(null);
  const [skuReasoning, setSkuReasoning] = useState({});
  const [loadingReasoning, setLoadingReasoning] = useState({});
  const [skuSourcing, setSkuSourcing] = useState({});
  const [loadingSourcing, setLoadingSourcing] = useState({});
  const [activeSourcingSku, setActiveSourcingSku] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'compliance_risk', 'savings', 'correct'
  const [decisionStep, setDecisionStep] = useState(1); // 1: Classification, 2: Landed Cost, 3: Sourcing
  const [landedCostData, setLandedCostData] = useState(null);
  const [showLandedCostModal, setShowLandedCostModal] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [teamShareLoading, setTeamShareLoading] = useState(false);
  const [isSharedWithTeam, setIsSharedWithTeam] = useState(false);
  // AI Improvement Tools state
  const [aiEnhanceLoading, setAiEnhanceLoading] = useState(false);
  const [aiHsHintLoading, setAiHsHintLoading] = useState(false);
  const [aiEnhancements, setAiEnhancements] = useState(null);
  const [aiHsSuggestions, setAiHsSuggestions] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  const [enhanceJobId, setEnhanceJobId] = useState(null);
  const [enhanceProgress, setEnhanceProgress] = useState({ processed: 0, total: 0 });
  const navigate = useNavigate();
  const reportRef = useRef(null);
  // Sourcing filter state
  const [sourcingFilters, setSourcingFilters] = useState({
    region_preference: 'any',
    max_lead_time: 'any',
    min_quality_tier: null,
    fta_only: false
  });

  // Generate AI executive narrative for financial impact
  const generateNarrative = async () => {
    if (!analysis || narrative) return;
    setNarrativeLoading(true);
    try {
      const data = await apiPost(`/api/financial/analyses/${analysisId}/narrative`);
      setNarrative(data.narrative);
    } catch (e) {
      toast.error('Failed to generate narrative');
    } finally {
      setNarrativeLoading(false);
    }
  };

  // Load cached narrative when analysis loads
  useEffect(() => {
    if (analysis?.executive_narrative) {
      setNarrative(analysis.executive_narrative);
    }
  }, [analysis]);

  // PDF Generation with proper formatting
  const handleDownloadPdf = async () => {
    if (!analysis) return;
    
    setPdfLoading(true);
    toast.info('Generating professional PDF report...');
    
    try {
      // Import the PDF generator dynamically
      const { generateAnalysisPdf, generateFinancialPdf } = await import('../utils/pdfGenerator');
      
      let pdf;
      if (analysis.type === 'financial_impact') {
        pdf = await generateFinancialPdf(analysis);
      } else {
        pdf = await generateAnalysisPdf(analysis, analysis.results || []);
      }
      
      const fileName = `Decoded_${analysis.type || 'Analysis'}_${analysisId}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };


  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      const label = analysis?.code_label || 'HS Code';
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Get status for a SKU
  const getSkuStatus = (sku) => {
    if (sku.tariff_delta > 0) return 'savings';
    if (sku.tariff_delta < 0 && sku.current_hs_code) return 'compliance_risk';
    if (sku.expected_hs_code && sku.current_hs_code && String(sku.current_hs_code).substring(0,6) !== String(sku.expected_hs_code).substring(0,6)) return 'compliance_risk';
    return 'correct';
  };

  useEffect(() => {
    // Reset all state when analysis changes (e.g., navigating from reclassified → original)
    setAnalysis(null);
    setLoading(true);
    setShareUrl(null);
    setShareLoading(false);
    setExpandedSku(null);
    setStatusFilter('all');
    setIsSharedWithTeam(false);
    setAiEnhancements(null);
    setAiHsSuggestions(null);
    setAiEnhanceLoading(false);
    setAiHsHintLoading(false);
    setReclassifyLoading(false);
    fetchAnalysis();
  }, [analysisId]);
  
  // Check if analysis is shared with team
  useEffect(() => {
    const checkTeamShare = async () => {
      try {
        const data = await apiGet('/api/team/shared');
        const isShared = data.shared_analyses?.some(s => s.analysis_id === analysisId);
        setIsSharedWithTeam(isShared);
      } catch (e) {
        // Ignore - user might not be in a team
      }
    };
    if (analysisId) checkTeamShare();
  }, [analysisId]);

  // Poll background enhance job
  useEffect(() => {
    if (!enhanceJobId) return;
    const interval = setInterval(async () => {
      try {
        const job = await apiGet(`/api/ai/enhance-job/${enhanceJobId}`);

        setEnhanceProgress({ processed: job.processed || 0, total: job.total || 0 });
        toast.loading(`Enhanced ${job.processed || 0}/${job.total || 0} descriptions...`, { id: 'enhance-progress' });

        if (job.status === 'complete') {
          clearInterval(interval);
          setEnhanceJobId(null);
          setAiEnhanceLoading(false);
          if (job.enhanced?.length > 0) {
            setAiEnhancements({ enhanced: job.enhanced, count: job.enhanced.length });
            toast.success(`Enhanced ${job.enhanced.length} descriptions!`, { id: 'enhance-progress' });
          } else {
            toast.info('No descriptions could be enhanced', { id: 'enhance-progress' });
          }
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setEnhanceJobId(null);
          setAiEnhanceLoading(false);
          toast.error(job.error || 'Enhancement job failed', { id: 'enhance-progress' });
        }
      } catch (e) {
        // Non-2xx or transient errors: stop polling on hard error, else keep going
        if (e?.status && e.status >= 400 && e.status < 500) {
          clearInterval(interval);
          setAiEnhanceLoading(false);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [enhanceJobId]);

  const fetchAnalysis = async () => {
    try {
      const data = await apiGet(`/api/analyses/${analysisId}`);
      setAnalysis(data);
      // Load saved landed cost data if exists
      if (data.landed_cost_data) {
        setLandedCostData(data.landed_cost_data);
        setDecisionStep(2); // Move to step 2 if landed cost already calculated
      }
    } catch (error) {
      toast.error(error?.status === 404 ? 'Analysis not found' : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchSkuReasoning = async (skuId) => {
    if (skuReasoning[skuId]) return;

    setLoadingReasoning(prev => ({ ...prev, [skuId]: true }));

    try {
      const data = await apiGet(`/api/analyses/${analysisId}/sku/${skuId}/reasoning`);
      setSkuReasoning(prev => ({ ...prev, [skuId]: data.detailed_reasoning }));
    } catch (error) {
      toast.error('Failed to load reasoning');
    } finally {
      setLoadingReasoning(prev => ({ ...prev, [skuId]: false }));
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiFetch(`/api/analyses/${analysisId}/export/csv`, { raw: true });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `decoded_analysis_${analysisId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('CSV exported successfully');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleDownloadCorrectedFile = async () => {
    try {
      const response = await apiFetch(`/api/analyses/${analysisId}/export/corrected-csv`, { raw: true });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `corrected_${analysisId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Corrected file downloaded');
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const toggleSkuExpand = (skuId) => {
    if (expandedSku === skuId) {
      setExpandedSku(null);
    } else {
      setExpandedSku(skuId);
      fetchSkuReasoning(skuId);
    }
  };

  const fetchSkuSourcing = async (skuId, filters = sourcingFilters) => {
    // Always refetch when filters change
    setLoadingSourcing(prev => ({ ...prev, [skuId]: true }));

    // Find the SKU data
    const skuData = analysis?.results?.find(r => r.sku_id === skuId);
    if (!skuData) {
      toast.error('SKU data not found');
      setLoadingSourcing(prev => ({ ...prev, [skuId]: false }));
      return;
    }

    try {
      const data = await apiPost('/api/analyze/sourcing', {
        description: skuData.description,
        country_of_origin: 'China', // Default, could be extracted from analysis
        import_country: 'United States',
        current_hs_code: skuData.current_hs_code,
        region_preference: filters.region_preference !== 'any' ? filters.region_preference : null,
        max_lead_time: filters.max_lead_time !== 'any' ? filters.max_lead_time : null,
        min_quality_tier: filters.min_quality_tier,
        fta_only: filters.fta_only
      });
      setSkuSourcing(prev => ({
        ...prev,
        [skuId]: {
          ...data,
          sourcing_alternatives: data.alternative_sourcing
        }
      }));
      setActiveSourcingSku(skuId);
    } catch (error) {
      toast.error('Failed to load sourcing alternatives');
    } finally {
      setLoadingSourcing(prev => ({ ...prev, [skuId]: false }));
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...sourcingFilters, [key]: value };
    setSourcingFilters(newFilters);
    // Refetch with new filters if a SKU is active
    if (activeSourcingSku) {
      fetchSkuSourcing(activeSourcingSku, newFilters);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#2563EB] animate-spin" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 font-jakarta">Analysis not found</p>
          <Link to="/dashboard">
            <Button className="mt-4 btn-primary-modern">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const results = analysis.results || [];
  
  // Calculate statistics
  const savingsItems = results.filter(r => r.tariff_delta > 0);
  const complianceRiskItems = results.filter(r => 
    (r.tariff_delta < 0 && r.current_hs_code) ||
    (r.expected_hs_code && r.current_hs_code && String(r.current_hs_code).substring(0,6) !== String(r.expected_hs_code).substring(0,6))
  );
  const correctItems = results.filter(r => 
    (r.tariff_delta === 0 || !r.current_hs_code) &&
    (!r.expected_hs_code || !r.current_hs_code || String(r.current_hs_code).substring(0,6) === String(r.expected_hs_code).substring(0,6))
  );
  
  const optimizedCount = savingsItems.length;
  const complianceRiskCount = complianceRiskItems.length;
  const optimizationPercentage = results.length > 0 ? Math.round((optimizedCount / results.length) * 100) : 0;
  const avgDelta = results.length > 0 ? results.reduce((sum, r) => sum + r.tariff_delta, 0) / results.length : 0;
  const totalSavings = results.reduce((sum, r) => sum + (r.estimated_savings > 0 ? r.estimated_savings : 0), 0);
  const totalComplianceExposure = Math.abs(results.reduce((sum, r) => sum + (r.estimated_savings < 0 ? r.estimated_savings : 0), 0));

  // Chart data
  const confidenceData = [
    { name: 'High', value: analysis.confidence_distribution?.high || results.filter(r => r.confidence_score >= 0.8).length, color: '#10B981' },
    { name: 'Medium', value: analysis.confidence_distribution?.medium || results.filter(r => r.confidence_score >= 0.5 && r.confidence_score < 0.8).length, color: '#F59E0B' },
    { name: 'Low', value: analysis.confidence_distribution?.low || results.filter(r => r.confidence_score < 0.5).length, color: '#EF4444' }
  ];

  const tariffComparisonData = results.slice(0, 10).map((r, i) => ({
    name: `SKU ${i + 1}`,
    current: r.tariff_current,
    suggested: r.tariff_suggested
  }));

  const savingsDistribution = results
    .filter(r => r.estimated_savings > 0)
    .sort((a, b) => (b.estimated_savings || 0) - (a.estimated_savings || 0))
    .slice(0, 10)
    .map((r, i) => ({
      name: `SKU ${i + 1}`,
      savings: r.estimated_savings
    }));

  const topOpportunities = results
    .filter(r => r.tariff_delta > 0)
    .sort((a, b) => (b.estimated_savings || b.tariff_delta) - (a.estimated_savings || a.tariff_delta))
    .slice(0, 5);

  // Additional calculations for bulk upload context
  const totalImportValue = results.reduce((sum, r) => sum + ((r.unit_cost || 0) * (r.annual_volume || 0)), 0);
  
  // Country breakdown
  const countryBreakdown = results.reduce((acc, r) => {
    const country = r.country_of_origin || 'Unknown';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {});
  const sortedCountries = Object.entries(countryBreakdown).sort((a, b) => b[1] - a[1]);

  // Priority actions
  const topRisks = complianceRiskItems
    .sort((a, b) => Math.abs(b.estimated_savings || 0) - Math.abs(a.estimated_savings || 0))
    .slice(0, 5);
  
  const quickWins = savingsItems
    .filter(r => r.confidence_score >= 0.8)
    .sort((a, b) => (b.estimated_savings || 0) - (a.estimated_savings || 0))
    .slice(0, 5);
  
  const needsReview = results
    .filter(r => r.confidence_score < 0.6)
    .slice(0, 5);

  // Copy all corrected codes
  const copyAllCodes = async () => {
    const codesText = results.map(r => `${r.sku_id}\t${r.suggested_hs_code}`).join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      toast.success(`${results.length} HS codes copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Generate shareable link
  const handleShare = async () => {
    setShareLoading(true);
    try {
      const isFinancial = analysis?.type === 'financial_impact';
      const shareEndpoint = isFinancial
        ? `/api/financial/analyses/${analysisId}/share`
        : `/api/analyses/${analysisId}/share`;

      const data = await apiPost(shareEndpoint);
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setShareUrl(fullUrl);
      try {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Share link copied to clipboard!');
      } catch {
        // Clipboard API may fail in non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Share link copied to clipboard!');
      }
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  // Share with team
  const handleTeamShare = async () => {
    setTeamShareLoading(true);
    try {
      await apiPost('/api/team/share', { analysis_id: analysisId });
      setIsSharedWithTeam(true);
      toast.success('Analysis shared with your team!');
    } catch (err) {
      if (err?.message?.includes('already shared')) {
        setIsSharedWithTeam(true);
        toast.info('Already shared with team');
      } else {
        toast.error(err?.message || 'Failed to share with team');
      }
    } finally {
      setTeamShareLoading(false);
    }
  };

  // ===================== AI IMPROVEMENT TOOLS =====================
  
  // Get description quality stats from analysis results
  const getResultsQualityStats = () => {
    if (!results || results.length <= 1) return null;
    
    let good = 0, fair = 0, poor = 0, missingHs = 0;
    const weakDescriptions = [];
    const missingHsItems = [];
    
    results.forEach((r, idx) => {
      const desc = r.description || '';
      const words = desc.trim().split(/\s+/);
      const hasNumbers = /\d/.test(desc);
      const hasMaterials = /(steel|plastic|metal|wood|glass|rubber|cotton|leather|aluminum|silicon|ceramic|fabric|polymer|alloy)/i.test(desc);
      const hasDimensions = /(inch|cm|mm|kg|lb|oz|gram|meter|foot|feet)/i.test(desc);
      
      let score = 0;
      if (words.length >= 5) score += 1;
      if (words.length >= 10) score += 1;
      if (words.length >= 15) score += 1;
      if (hasNumbers) score += 1;
      if (hasMaterials) score += 1;
      if (hasDimensions) score += 1;
      
      if (score >= 4) good++;
      else if (score >= 2) { fair++; weakDescriptions.push({ index: idx, sku_id: r.sku_id, description: desc }); }
      else if (words.length > 0) { poor++; weakDescriptions.push({ index: idx, sku_id: r.sku_id, description: desc }); }
      
      if (!r.current_hs_code || !r.current_hs_code.trim()) {
        missingHs++;
        missingHsItems.push({ index: idx, sku_id: r.sku_id, description: desc });
      }
    });
    
    return { total: results.length, good, fair, poor, missingHs, weakDescriptions, missingHsItems };
  };

  // AI Enhance Descriptions — scope-based: 'preview' (10), 'sample' (50), or 'all'
  const handleResultsEnhance = async (scope = 'all') => {
    const stats = getResultsQualityStats();
    if (!stats || stats.weakDescriptions.length === 0) {
      toast.info('All descriptions are already high quality');
      return;
    }

    const allWeak = stats.weakDescriptions;
    const limit = scope === 'preview' ? 10 : scope === 'sample' ? 50 : allWeak.length;
    const itemsToProcess = allWeak.slice(0, limit);

    // For large sets (>50), use background job
    if (itemsToProcess.length > 50) {
      return handleEnhanceBackground(itemsToProcess);
    }

    // Inline batching for ≤50 items
    setAiEnhanceLoading(true);
    setEnhanceProgress({ processed: 0, total: itemsToProcess.length });
    const batchSize = 10;
    const totalBatches = Math.ceil(itemsToProcess.length / batchSize);
    let allEnhanced = [];

    try {
      for (let batch = 0; batch < totalBatches; batch++) {
        const batchItems = itemsToProcess.slice(batch * batchSize, (batch + 1) * batchSize);
        setEnhanceProgress({ processed: allEnhanced.length, total: itemsToProcess.length });
        toast.loading(`Enhancing ${allEnhanced.length + batchItems.length}/${itemsToProcess.length}...`, { id: 'enhance-progress' });

        try {
          const data = await apiPost('/api/ai/bulk-enhance', {
            descriptions: batchItems.map(r => ({
              index: r.index,
              description: r.description
            }))
          });
          if (data.enhanced) {
            allEnhanced = [...allEnhanced, ...data.enhanced];
          }
        } catch (batchErr) {
          toast.error(batchErr?.message || `Batch ${batch + 1} failed`, { id: 'enhance-progress' });
          break;
        }
      }

      if (allEnhanced.length > 0) {
        setAiEnhancements({ enhanced: allEnhanced, count: allEnhanced.length });
        setEnhanceProgress({ processed: allEnhanced.length, total: itemsToProcess.length });
        toast.success(`Enhanced ${allEnhanced.length} descriptions!`, { id: 'enhance-progress' });
      }
    } catch (error) {
      if (allEnhanced.length > 0) {
        setAiEnhancements({ enhanced: allEnhanced, count: allEnhanced.length });
        toast.warning(`Enhanced ${allEnhanced.length}/${itemsToProcess.length} before connection issue.`, { id: 'enhance-progress' });
      } else {
        toast.error('Connection error - please try again', { id: 'enhance-progress' });
      }
    } finally {
      setAiEnhanceLoading(false);
    }
  };

  // Background job for large enhancement sets (>50 items)
  const handleEnhanceBackground = async (items) => {
    setAiEnhanceLoading(true);
    setEnhanceProgress({ processed: 0, total: items.length });

    try {
      const data = await apiPost('/api/ai/enhance-job', {
        analysis_id: analysisId || 'unknown',
        descriptions: items.map(r => ({ index: r.index, description: r.description }))
      });
      setEnhanceJobId(data.job_id);
      toast.loading(`Enhancement started — processing ${items.length} descriptions...`, { id: 'enhance-progress' });
    } catch (e) {
      toast.error(e?.message || 'Failed to start enhancement job');
      setAiEnhanceLoading(false);
    }
  };

  // AI Suggest HS Codes on results page
  const handleResultsHsHint = async () => {
    const stats = getResultsQualityStats();
    if (!stats || stats.missingHsItems.length === 0) {
      toast.info('All rows already have HS codes');
      return;
    }

    setAiHsHintLoading(true);
    const toSuggest = stats.missingHsItems.filter(r => r.description.trim().length >= 10).slice(0, 15);

    if (toSuggest.length === 0) {
      toast.info('Descriptions too brief for HS code suggestions');
      setAiHsHintLoading(false);
      return;
    }

    try {
      const data = await apiPost('/api/ai/bulk-hs-hint', {
        items: toSuggest.map(r => ({
          index: r.index,
          description: r.description
        }))
      });
      if (data.suggestions) {
        setAiHsSuggestions(data);
        toast.success(`Generated ${data.suggestions.length} HS code suggestions! Review below.`);
      } else {
        toast.error('HS code suggestion failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Connection error');
    } finally {
      setAiHsHintLoading(false);
    }
  };

  // Re-classify with AI improvements
  const handleReclassify = async () => {
    const enhancedDescriptions = {};
    const suggestedHsCodes = {};

    if (aiEnhancements?.enhanced) {
      aiEnhancements.enhanced.forEach(item => {
        enhancedDescriptions[String(item.index)] = item.enhanced_description;
      });
    }

    if (aiHsSuggestions?.suggestions) {
      aiHsSuggestions.suggestions.forEach(item => {
        suggestedHsCodes[String(item.index)] = item.suggested_hs_code;
      });
    }

    if (Object.keys(enhancedDescriptions).length === 0 && Object.keys(suggestedHsCodes).length === 0) {
      toast.info('Run AI Enhance or Suggest HS Codes first');
      return;
    }

    setReclassifyLoading(true);

    try {
      const data = await apiPost(`/api/analyze/reclassify/${analysisId}`, {
        enhanced_descriptions: enhancedDescriptions,
        suggested_hs_codes: suggestedHsCodes
      });
      if (data.analysis_id) {
        const { descriptions_enhanced, hs_codes_added } = data.improvements_applied || {};
        toast.success(`Re-classified ${data.total_skus} SKUs with ${descriptions_enhanced || 0} enhanced descriptions and ${hs_codes_added || 0} HS codes applied`);
        navigate(`/analysis/${data.analysis_id}`);
      } else {
        toast.error('Re-classification failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Connection error during re-classification');
    } finally {
      setReclassifyLoading(false);
    }
  };

  // ===================== END AI IMPROVEMENT TOOLS =====================

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <Sidebar user={user} activePage="analysis" />
      
      <main className="flex-1 p-6 lg:p-8 lg:pt-24">
        <div className="max-w-7xl mx-auto" ref={reportRef}>
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Link to="/dashboard">
                  <Button variant="ghost" size="icon" className="rounded-xl hover:bg-slate-100" data-testid="back-btn">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                </Link>
                <div>
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB] px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    <Sparkles className="w-3 h-3" />
                    Analysis Complete
                  </div>
                  <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900" data-testid="analysis-title">
                    Classification Results
                  </h1>
                  <p className="text-slate-500 text-sm font-jakarta">
                    {analysis.name || (results.length === 1 ? 'Single Product Analysis' : 'Bulk Catalog Analysis')} • {new Date(analysis.created_at).toLocaleDateString()}
                  </p>
                  {analysis.reclassified_from && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-[#6366F1]/10 to-[#8B5CF6]/10 text-[#6366F1] border border-[#6366F1]/20" data-testid="reclassified-badge">
                        <RefreshCw className="w-3 h-3" />
                        Re-classified with AI improvements
                      </span>
                      <Link to={`/analysis/${analysis.reclassified_from}`} className="text-xs text-slate-400 hover:text-[#6366F1] underline">
                        View original
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button 
                  onClick={handleTeamShare}
                  variant="outline"
                  className={`rounded-xl ${isSharedWithTeam ? 'border-green-500/30 text-green-600 bg-green-50' : 'border-purple-500/30 text-purple-600 hover:bg-purple-50'}`}
                  disabled={teamShareLoading || isSharedWithTeam}
                  data-testid="team-share-btn"
                >
                  {teamShareLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : isSharedWithTeam ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  {isSharedWithTeam ? 'Shared with Team' : 'Share with Team'}
                </Button>
                <Button 
                  onClick={handleShare}
                  variant="outline"
                  className="rounded-xl border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/5"
                  disabled={shareLoading}
                  data-testid="share-btn"
                >
                  {shareLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : shareUrl ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-2" />
                  )}
                  {shareUrl ? 'Link Copied!' : 'Share Report'}
                </Button>
              </div>
            </div>

            {/* Before vs After Comparison Banner — full width below header */}
            {analysis.reclassified_from && analysis.original_metrics && (
              <div className="mt-5 rounded-xl bg-gradient-to-r from-[#6366F1]/5 via-white to-[#8B5CF6]/5 border border-[#6366F1]/20 p-5" data-testid="before-after-banner">
                <div className="flex items-center gap-2 mb-4">
                  <RefreshCw className="w-4 h-4 text-[#6366F1]" />
                  <span className="font-outfit font-semibold text-sm text-slate-900">Before vs After AI Reclassification</span>
                  {analysis.improvements_applied && (
                    <span className="ml-auto text-xs text-slate-500">
                      {analysis.improvements_applied.descriptions_enhanced || 0} descriptions enhanced, {analysis.improvements_applied.hs_codes_added || 0} HS codes added
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-3 border border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Compliance Issues</p>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-[#EF4444]">{analysis.original_metrics.compliance_issues || 0}</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-lg font-bold text-[#6366F1]">{complianceRiskItems.length}</span>
                      {(analysis.original_metrics.compliance_issues || 0) > complianceRiskItems.length && (
                        <span className="text-xs font-semibold text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5 rounded">
                          -{(analysis.original_metrics.compliance_issues || 0) - complianceRiskItems.length} resolved
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Compliance Exposure</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#EF4444]">${Math.round((analysis.original_metrics.compliance_exposure || 0) / 1000)}K</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-sm font-bold text-[#6366F1]">${Math.round((analysis.compliance_exposure || 0) / 1000)}K</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Total Savings</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-500">${Math.round((analysis.original_metrics.total_estimated_savings || 0) / 1000)}K</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-sm font-bold text-[#10B981]">${Math.round((analysis.total_estimated_savings || 0) / 1000)}K</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Optimized SKUs</p>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-slate-500">{analysis.original_metrics.optimized_skus || 0}</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-lg font-bold text-[#10B981]">{savingsItems.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ═══════════════════ SECTION 1: YOUR RESULTS ═══════════════════ */}

          {/* Compact Executive Summary */}
          <div className={`grid ${results.length > 1 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6' : 'grid-cols-2 sm:grid-cols-4'} gap-3 mb-8`}>
            <div className="card-modern p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setStatusFilter('all')}>
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-[#2563EB]" />
                <span className="text-xs text-slate-500">SKUs Analyzed</span>
              </div>
              <p className="font-outfit text-2xl font-bold text-slate-900" data-testid="total-skus">{analysis.total_skus || results.length}</p>
            </div>

            <div className="card-modern p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => totalSavings > 0 && setStatusFilter(statusFilter === 'savings' ? 'all' : 'savings')}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-[#10B981]" />
                <span className="text-xs text-slate-500">Potential Savings</span>
              </div>
              <p className="font-outfit text-2xl font-bold text-[#10B981]" data-testid="total-savings">
                {totalSavings > 0 ? `$${totalSavings.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            {(results.length > 1 || complianceRiskCount > 0) && (
              <div className={`card-modern p-4 cursor-pointer hover:shadow-lg transition-shadow ${complianceRiskCount > 0 ? 'border-2 border-[#EF4444]/30 bg-[#EF4444]/5' : ''}`} onClick={() => complianceRiskCount > 0 && setStatusFilter(statusFilter === 'compliance_risk' ? 'all' : 'compliance_risk')}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className={`w-5 h-5 ${complianceRiskCount > 0 ? 'text-[#EF4444]' : 'text-slate-400'}`} />
                  <span className="text-xs text-slate-500">Compliance Risks</span>
                </div>
                <p className={`font-outfit text-2xl font-bold ${complianceRiskCount > 0 ? 'text-[#EF4444]' : 'text-slate-400'}`} data-testid="compliance-exposure">
                  {complianceRiskCount > 0 ? complianceRiskCount : '0'}
                </p>
              </div>
            )}

            <div className="card-modern p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-xs text-slate-500">Optimization</span>
              </div>
              <p className="font-outfit text-2xl font-bold text-slate-900" data-testid="optimization-rate">{optimizationPercentage}%</p>
            </div>

            {results.length > 1 && (
              <>
                <div className="card-modern p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-[#6366F1]" />
                    <span className="text-xs text-slate-500">Avg. Tariff Delta</span>
                  </div>
                  <p className="font-outfit text-2xl font-bold text-slate-900" data-testid="avg-delta">{avgDelta.toFixed(2)}%</p>
                </div>

                <div className="card-modern p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-5 h-5 text-[#8B5CF6]" />
                    <span className="text-xs text-slate-500">Countries</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {sortedCountries.slice(0, 2).map(([country, count]) => (
                      <span key={country} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{country} ({count})</span>
                    ))}
                    {sortedCountries.length > 2 && <span className="text-xs text-slate-400">+{sortedCountries.length - 2}</span>}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ═══════════════════ EXECUTIVE NARRATIVE (Financial Impact only) ═══════════════════ */}
          {analysis?.type === 'financial_impact' && (
            <div className="mb-8 card-modern p-6" data-testid="executive-narrative-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-slate-900">Executive Narrative</h3>
                    <p className="text-xs text-slate-500">AI-generated briefing for stakeholders</p>
                  </div>
                </div>
                {!narrative && (
                  <Button
                    onClick={generateNarrative}
                    disabled={narrativeLoading}
                    variant="outline"
                    className="gap-2 border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/5"
                    data-testid="generate-narrative-btn"
                  >
                    {narrativeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Generate Narrative
                  </Button>
                )}
              </div>
              {narrativeLoading && (
                <div className="flex items-center gap-3 py-6 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Generating executive briefing...</span>
                </div>
              )}
              {narrative && (
                <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed bg-gradient-to-r from-slate-50 to-white rounded-xl p-5 border border-slate-100">
                  {narrative.split('\n\n').map((para, i) => (
                    <p key={i} className="mb-3 last:mb-0">{para}</p>
                  ))}
                </div>
              )}
              {!narrative && !narrativeLoading && (
                <p className="text-sm text-slate-400 italic">Click &quot;Generate Narrative&quot; to create an executive summary for this analysis.</p>
              )}
            </div>
          )}

          {/* ═══════════════════ SECTION 2: WHAT WE FOUND ═══════════════════ */}

          {/* Integrated Decision Flow - Compact Next Steps for single product */}
          {results.length === 1 && (
            <div className="mb-6">
              <div className="card-modern p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900 text-sm">Next Steps</span>
                      <p className="text-xs text-slate-500">
                        Code: <span className="font-mono font-semibold text-[#2563EB]">{results[0]?.suggested_hs_code}</span> at <span className="font-semibold">{results[0]?.tariff_suggested}%</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <LandedCostCalculator
                      dutyRate={results[0]?.tariff_current || 0}
                      suggestedDutyRate={results[0]?.tariff_suggested || 0}
                      productCost={results[0]?.unit_cost || 0}
                      originCountry={results[0]?.country_of_origin || ''}
                      destinationCountry={results[0]?.import_country || 'United States'}
                      alternativeOrigins={results[0]?.sourcing_alternatives || null}
                      analysisId={analysisId}
                      onComplete={(data) => { setLandedCostData(data); setDecisionStep(2); }}
                      trigger={
                        <Button variant="outline" size="sm" className="text-xs" data-testid="journey-landed-cost-btn">
                          <Calculator className="w-3.5 h-3.5 mr-1.5" />
                          {landedCostData ? 'Recalculate Landed Cost' : 'Calculate Landed Cost'}
                        </Button>
                      }
                    />
                    <Button 
                      size="sm"
                      className="bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] text-white text-xs"
                      onClick={() => {
                        if (skuSourcing[results[0]?.sku_id]) {
                          const el = document.querySelector('[data-testid="sourcing-section"]');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          fetchSkuSourcing(results[0]?.sku_id);
                        }
                      }}
                      disabled={loadingSourcing[results[0]?.sku_id]}
                      data-testid="journey-sourcing-btn"
                    >
                      {loadingSourcing[results[0]?.sku_id] ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Finding...</>
                      ) : (
                        <><Globe className="w-3.5 h-3.5 mr-1.5" />Compare Sourcing</>
                      )}
                    </Button>
                  </div>
                </div>
                {landedCostData && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-2.5 flex items-center gap-4 text-xs">
                    <div><span className="text-slate-500">Product</span><span className="ml-1.5 font-semibold">{landedCostData.currency_symbol}{landedCostData.product_cost_per_unit.toFixed(2)}</span></div>
                    <span className="text-slate-300">+</span>
                    <div><span className="text-slate-500">Shipping</span><span className="ml-1.5 font-semibold">{landedCostData.currency_symbol}{landedCostData.shipping_cost_per_unit.toFixed(2)}</span></div>
                    <span className="text-slate-300">+</span>
                    <div><span className="text-slate-500">Duty ({landedCostData.duty_rate_percent}%)</span><span className="ml-1.5 font-semibold text-red-600">{landedCostData.currency_symbol}{landedCostData.duty_cost_per_unit.toFixed(2)}</span></div>
                    <span className="text-slate-300">=</span>
                    <div><span className="text-slate-500">Landed</span><span className="ml-1.5 font-bold text-[#059669]">{landedCostData.currency_symbol}{landedCostData.landed_cost_per_unit.toFixed(2)}</span></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Priority Findings - For bulk */}
          {results.length > 1 && (complianceRiskCount > 0 || savingsItems.length > 0) && (
            <div className="card-modern p-6 mb-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EF4444] to-[#F59E0B] flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-slate-900">What We Found</h3>
                  <p className="text-xs text-slate-500">
                    {complianceRiskCount > 0 && `${complianceRiskCount} compliance risk${complianceRiskCount > 1 ? 's' : ''} costing you ~$${totalComplianceExposure.toLocaleString()}/yr`}
                    {complianceRiskCount > 0 && savingsItems.length > 0 && ' • '}
                    {savingsItems.length > 0 && `${savingsItems.length} savings opportunities worth ~$${totalSavings.toLocaleString()}/yr`}
                  </p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5">
                {/* Compliance Risks */}
                {topRisks.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#EF4444] mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> TOP COMPLIANCE RISKS
                    </p>
                    <div className="space-y-2">
                      {topRisks.map((sku, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#EF4444]/5 rounded-lg p-2.5 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#EF4444] text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                            <span className="text-slate-700 truncate max-w-[200px]">{sku.description}</span>
                          </div>
                          <span className="font-bold text-[#EF4444]">+${Math.abs(sku.estimated_savings || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Wins */}
                {quickWins.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#10B981] mb-2 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> QUICK WINS (High Confidence)
                    </p>
                    <div className="space-y-2">
                      {quickWins.slice(0, 5).map((sku, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#10B981]/5 rounded-lg p-2.5 text-sm">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-[#10B981]" />
                            <span className="text-slate-700 truncate max-w-[200px]">{sku.description}</span>
                          </div>
                          <span className="font-bold text-[#10B981]">-${(sku.estimated_savings || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {needsReview.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-semibold text-[#F59E0B] flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {needsReview.length} SKU{needsReview.length > 1 ? 's' : ''} with low confidence — recommend manual verification
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Classification Comparison - Single/few products */}
          {results.length <= 5 && results.some(r => r.current_hs_code) && (
            <div className="mb-6 space-y-4">
              {results.filter(r => r.current_hs_code).map((sku, idx) => (
                <div key={idx} className="card-modern p-6" data-testid={`classification-comparison-${idx}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <ArrowRightLeft className="w-5 h-5 text-[#6366F1]" />
                    <h3 className="font-outfit font-semibold text-slate-900">Classification Analysis</h3>
                    <span className="text-sm text-slate-500">— {sku.description}</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div className={`rounded-xl p-4 border-2 ${sku.tariff_delta < 0 ? 'bg-[#F59E0B]/5 border-[#F59E0B]/30' : 'bg-[#EF4444]/5 border-[#EF4444]/30'}`}>
                      <p className={`text-xs font-bold mb-2 ${sku.tariff_delta < 0 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>{sku.tariff_delta < 0 ? 'CURRENT (Underpaying)' : 'CURRENT (Overpaying)'}</p>
                      <p className="font-mono text-2xl font-bold text-slate-800">{sku.current_hs_code}</p>
                      <p className="text-lg text-slate-600 mt-2">Rate: <span className={`font-bold ${sku.tariff_delta < 0 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>{sku.tariff_current}%</span></p>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <ArrowRight className="w-10 h-10 text-[#2563EB]" />
                      <div className={`mt-2 px-4 py-2 rounded-full text-sm font-bold ${sku.tariff_delta > 0 ? 'bg-[#10B981]/10 text-[#10B981]' : sku.tariff_delta < 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-slate-100 text-slate-600'}`}>
                        {sku.tariff_delta > 0 ? `Save ${sku.tariff_delta.toFixed(1)}%` : sku.tariff_delta < 0 ? `+${Math.abs(sku.tariff_delta).toFixed(1)}% More` : 'No Change'}
                      </div>
                    </div>
                    <div className="bg-[#10B981]/5 rounded-xl p-4 border-2 border-[#10B981]/30">
                      <p className="text-xs font-bold text-[#10B981] mb-2">CORRECT CODE</p>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-2xl font-bold text-slate-800">{sku.suggested_hs_code}</p>
                        <button onClick={() => copyToClipboard(sku.suggested_hs_code)} className="p-2 rounded-lg hover:bg-[#10B981]/20 transition-colors group" data-testid={`copy-hs-code-${idx}`}>
                          {copiedCode === sku.suggested_hs_code ? <Check className="w-5 h-5 text-[#10B981]" /> : <Copy className="w-5 h-5 text-slate-400 group-hover:text-[#10B981]" />}
                        </button>
                      </div>
                      <p className="text-lg text-slate-600 mt-2">Rate: <span className="font-bold text-[#10B981]">{sku.tariff_suggested}%</span></p>
                    </div>
                  </div>

                  {sku.estimated_savings !== null && sku.estimated_savings !== 0 && (
                    <div className={`rounded-xl p-4 mb-4 ${sku.tariff_delta > 0 ? 'bg-gradient-to-r from-[#10B981]/10 to-[#06B6D4]/10 border border-[#10B981]/20' : 'bg-gradient-to-r from-[#EF4444]/10 to-[#F59E0B]/10 border border-[#EF4444]/30'}`}>
                      {sku.tariff_delta > 0 ? (
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-[#10B981]/20 flex items-center justify-center"><DollarSign className="w-6 h-6 text-[#10B981]" /></div>
                          <div><p className="text-sm text-slate-600">Estimated Annual Savings</p><p className="text-2xl font-bold text-[#10B981]">${sku.estimated_savings.toLocaleString()}</p></div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#EF4444]/20 flex items-center justify-center flex-shrink-0"><AlertCircle className="w-6 h-6 text-[#EF4444]" /></div>
                            <div>
                              <p className="text-lg font-bold text-[#EF4444]">COMPLIANCE RISK DETECTED</p>
                              <p className="text-sm text-slate-600 mt-1">Your current classification results in <strong>underpayment of duties</strong> by approximately:</p>
                              <p className="text-2xl font-bold text-[#EF4444] mt-1">${Math.abs(sku.estimated_savings).toLocaleString()}/year</p>
                            </div>
                          </div>
                          <div className="bg-white/60 rounded-lg p-3 mt-2">
                            <p className="text-sm text-slate-700 font-medium">What this means:</p>
                            <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                              <li>CBP may assess <strong>back duties</strong> for up to 5 years</li>
                              <li>Penalties can range from <strong>20% to 400%</strong> of unpaid duties</li>
                              <li>Interest accrues on all unpaid amounts</li>
                            </ul>
                            <p className="text-sm font-semibold text-[#2563EB] mt-3">Recommendation: Consult with a licensed customs broker and consider a Prior Disclosure to CBP.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expected HS Code comparison when available */}
                  {sku.expected_hs_code && String(sku.current_hs_code || '').substring(0,6) !== String(sku.expected_hs_code).substring(0,6) && (
                    <div className="rounded-xl p-3 mb-4 bg-gradient-to-r from-[#6366F1]/5 to-[#8B5CF6]/5 border border-[#6366F1]/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-[#6366F1]" />
                        <span className="text-xs font-semibold text-[#6366F1]">Expected {analysis?.code_label || 'HS Code'} from ERP</span>
                      </div>
                      <p className="font-mono text-sm font-semibold text-slate-800 ml-6">{sku.expected_hs_code}</p>
                      <p className="text-xs text-slate-500 ml-6 mt-1">
                        Declared code ({sku.current_hs_code}) differs from expected classification
                      </p>
                    </div>
                  )}

                  {/* AI Reasoning */}
                  <div className="p-4 bg-gradient-to-r from-slate-50 to-[#6366F1]/5 rounded-xl border border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-[#6366F1]" />
                        <p className="text-sm font-semibold text-slate-900">Why This Code?</p>
                      </div>
                      <AIFeedback analysisId={analysisId} skuId={sku.sku_id} />
                    </div>
                    {sku.reasoning ? (
                      <div className="space-y-3">
                        <p className="text-sm text-slate-600 leading-relaxed">{sku.reasoning}</p>
                        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${sku.confidence_score >= 0.8 ? 'bg-emerald-500' : sku.confidence_score >= 0.5 ? 'bg-amber-500' : 'bg-red-500'}`} />
                            <span className="text-xs text-slate-500">Confidence: <span className="font-semibold text-slate-700">{Math.round(sku.confidence_score * 100)}%</span></span>
                          </div>
                          {sku.tariff_delta !== 0 && (
                            <div className="flex items-center gap-1.5">
                              <DollarSign className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-500">Impact: <span className={`font-semibold ${sku.tariff_delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{sku.tariff_delta > 0 ? '-' : '+'}{Math.abs(sku.tariff_delta).toFixed(1)}% duty</span></span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">Classification based on product description analysis and HS code matching algorithms.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tariff Comparison Chart - Inline with findings */}
          {results.length > 1 && (
            <div className="card-modern p-6 mb-6">
              <h3 className="font-outfit text-base font-semibold text-slate-900 mb-4">Current vs Suggested Tariff Rates</h3>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tariffComparisonData} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" unit="%" />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} formatter={(value) => [`${value}%`]} />
                    <Legend />
                    <Bar dataKey="current" name="Current Tariff" fill="#2563EB" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="suggested" name="Suggested Tariff" fill="#10B981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {/* ═══════════════════ SECTION 3: WHAT YOU CAN DO ═══════════════════ */}

          {/* AI Improvement Tools - For multi-SKU analyses (bulk + ERP) */}
          {results.length > 1 && (() => {
            const isReclassified = !!analysis.reclassified_from;
            const originalResults = analysis.original_results || [];
            const improvementsApplied = analysis.improvements_applied || {};
            
            // For reclassified: show comparison view
            if (isReclassified && originalResults.length > 0) {
              const changedSkus = results.map((r, idx) => {
                const orig = originalResults[idx];
                if (!orig) return null;
                const descChanged = orig.description !== r.description;
                const codeChanged = orig.suggested_hs_code !== r.suggested_hs_code;
                const hsAdded = !orig.current_hs_code && r.current_hs_code;
                if (!descChanged && !codeChanged && !hsAdded) return null;
                return { idx, orig, current: r, descChanged, codeChanged, hsAdded };
              }).filter(Boolean);

              if (changedSkus.length === 0) return null;

              return (
                <div className="card-modern p-6 mb-8" data-testid="ai-improvement-tools">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                      <RefreshCw className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-outfit font-semibold text-slate-900">Before vs After — AI Improvements</h3>
                      <p className="text-xs text-slate-500">
                        {improvementsApplied.descriptions_enhanced || 0} descriptions enhanced
                        {improvementsApplied.hs_codes_added > 0 && `, ${improvementsApplied.hs_codes_added} HS codes added`}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {changedSkus.map(({ idx, orig, current, descChanged, codeChanged, hsAdded }) => (
                      <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid={`comparison-${idx}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs font-semibold text-slate-500 bg-white px-2 py-0.5 rounded">{current.sku_id}</span>
                          <div className="flex gap-1.5">
                            {descChanged && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#6366F1]/10 text-[#6366F1] font-medium">Description improved</span>}
                            {(codeChanged || hsAdded) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-medium">Code changed</span>}
                          </div>
                        </div>

                        {descChanged && (
                          <div className="grid md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <p className="text-xs font-medium text-red-500 mb-1">Original Description</p>
                              <p className="text-sm text-slate-600 bg-red-50/50 rounded-lg p-2">{orig.description}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-green-600 mb-1">Enhanced Description</p>
                              <p className="text-sm text-slate-800 bg-green-50/50 rounded-lg p-2">{current.description}</p>
                            </div>
                          </div>
                        )}

                        {(codeChanged || hsAdded) && (
                          <div className="grid md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs font-medium text-slate-500 mb-1">Original Suggested Code</p>
                              <span className="font-mono text-sm font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                {orig.suggested_hs_code || '—'}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-[#8B5CF6] mb-1">New Suggested Code</p>
                              <span className="font-mono text-sm font-semibold text-[#8B5CF6] bg-[#8B5CF6]/10 px-2 py-0.5 rounded">
                                {current.suggested_hs_code || '—'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // For non-reclassified: show Step 1 / Step 2 action flow
            const qualityStats = getResultsQualityStats();
            if (!qualityStats) return null;
            const hasWeakDescriptions = (qualityStats.fair + qualityStats.poor) > 0;
            const hasMissingHs = qualityStats.missingHs > 0;
            if (!hasWeakDescriptions && !hasMissingHs) return null;

            const hasImprovements = (aiEnhancements?.enhanced?.length > 0 || aiHsSuggestions?.suggestions?.length > 0);
            
            return (
              <div className="card-modern p-6 mb-8" data-testid="ai-improvement-tools">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-slate-900">AI Improvement Tools</h3>
                    <p className="text-xs text-slate-500">Enhance your data quality based on classification findings</p>
                  </div>
                </div>

                {/* Quality Stats Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-xl font-bold text-slate-900">{qualityStats.total}</p>
                    <p className="text-xs text-slate-500">Total SKUs</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-xl">
                    <p className="text-xl font-bold text-green-600">{qualityStats.good}</p>
                    <p className="text-xs text-green-600">Good Descriptions</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-xl">
                    <p className="text-xl font-bold text-amber-600">{qualityStats.fair}</p>
                    <p className="text-xs text-amber-600">Fair</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-xl">
                    <p className="text-xl font-bold text-red-500">{qualityStats.poor}</p>
                    <p className="text-xs text-red-500">Brief</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-xl">
                    <p className="text-xl font-bold text-purple-600">{qualityStats.missingHs}</p>
                    <p className="text-xs text-purple-600">Missing {analysis?.code_label || 'HS Code'}s</p>
                  </div>
                </div>

                {/* Step 1 & Step 2 */}
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className={`rounded-xl border p-4 ${hasImprovements ? 'border-green-200 bg-green-50/30' : 'border-[#6366F1]/20 bg-[#6366F1]/5'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${hasImprovements ? 'bg-green-500 text-white' : 'bg-[#6366F1] text-white'}`}>
                        {hasImprovements ? <Check className="w-3.5 h-3.5" /> : '1'}
                      </span>
                      <span className="text-sm font-semibold text-slate-800">
                        {hasImprovements ? 'Improvements generated' : 'Generate AI improvements'}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {hasWeakDescriptions && !aiEnhancements?.enhanced?.length && (
                        <div className="flex flex-col gap-2 w-full">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              onClick={() => handleResultsEnhance('preview')}
                              disabled={aiEnhanceLoading}
                              variant="outline"
                              size="sm"
                              className="border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10"
                              data-testid="enhance-preview-btn"
                            >
                              {aiEnhanceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                              Quick Preview (10)
                            </Button>
                            {(qualityStats.fair + qualityStats.poor) > 10 && (
                              <Button
                                onClick={() => handleResultsEnhance('sample')}
                                disabled={aiEnhanceLoading}
                                variant="outline"
                                size="sm"
                                className="border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10"
                                data-testid="enhance-sample-btn"
                              >
                                {aiEnhanceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                                Sample (50)
                              </Button>
                            )}
                            <Button
                              onClick={() => handleResultsEnhance('all')}
                              disabled={aiEnhanceLoading}
                              className="bg-[#6366F1] hover:bg-[#5558E6] text-white"
                              size="sm"
                              data-testid="enhance-all-btn"
                            >
                              {aiEnhanceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                              Enhance All ({qualityStats.fair + qualityStats.poor})
                            </Button>
                          </div>
                          {aiEnhanceLoading && enhanceProgress.total > 0 && (
                            <div className="w-full" data-testid="enhance-progress-bar">
                              <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Enhancing descriptions...</span>
                                <span>{enhanceProgress.processed}/{enhanceProgress.total}</span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-[#6366F1] h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${Math.round((enhanceProgress.processed / enhanceProgress.total) * 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {hasWeakDescriptions && aiEnhancements?.enhanced?.length > 0 && (
                        <Button
                          disabled
                          variant="outline"
                          className="border-green-300 text-green-600 opacity-80"
                          data-testid="results-enhance-btn"
                        >
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          {aiEnhancements.enhanced.length} Descriptions Enhanced
                        </Button>
                      )}
                      {hasMissingHs && (
                        <Button
                          onClick={handleResultsHsHint}
                          disabled={aiHsHintLoading || (aiHsSuggestions?.suggestions?.length > 0)}
                          variant="outline"
                          className={`border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 ${aiHsSuggestions?.suggestions?.length > 0 ? 'opacity-60' : ''}`}
                          data-testid="results-hs-hint-btn"
                        >
                          {aiHsHintLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : aiHsSuggestions?.suggestions?.length > 0 ? (
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                          ) : (
                            <Zap className="w-4 h-4 mr-2" />
                          )}
                          {aiHsHintLoading ? 'Suggesting...' : aiHsSuggestions?.suggestions?.length > 0 ? `${aiHsSuggestions.suggestions.length} HS Codes Suggested` : `Suggest ${Math.min(qualityStats.missingHs, 15)} HS Codes`}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* AI Enhancement Results (inline between steps) */}
                  {aiEnhancements && aiEnhancements.enhanced && aiEnhancements.enhanced.length > 0 && (
                    <div className="pl-4 border-l-2 border-[#6366F1]/20">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#6366F1]" />
                        Enhanced Descriptions ({aiEnhancements.enhanced.length})
                      </h4>
                      <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                        {aiEnhancements.enhanced.map((item, idx) => {
                          const sku = results[item.index];
                          return (
                            <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid={`enhancement-${idx}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold text-slate-500 bg-white px-2 py-0.5 rounded">{sku?.sku_id || `Row ${item.index + 1}`}</span>
                              </div>
                              <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs font-medium text-red-500 mb-1">Original</p>
                                  <p className="text-sm text-slate-600 bg-red-50/50 rounded-lg p-2">{item.original}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-green-600 mb-1">AI Enhanced</p>
                                  <p className="text-sm text-slate-800 bg-green-50/50 rounded-lg p-2">{item.enhanced_description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* AI HS Code Suggestions (inline between steps) */}
                  {aiHsSuggestions && aiHsSuggestions.suggestions && aiHsSuggestions.suggestions.length > 0 && (
                    <div className="pl-4 border-l-2 border-[#8B5CF6]/20">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[#8B5CF6]" />
                        Suggested {analysis?.code_label || 'HS Code'}s ({aiHsSuggestions.suggestions.length})
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">SKU</th>
                              <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">Description</th>
                              <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">Suggested {analysis?.code_label || 'HS Code'}</th>
                              <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">Category</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aiHsSuggestions.suggestions.map((item, idx) => {
                              const sku = results[item.index];
                              return (
                                <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`hs-suggestion-${idx}`}>
                                  <td className="py-2 px-3 text-slate-700 font-medium">{sku?.sku_id || `Row ${item.index + 1}`}</td>
                                  <td className="py-2 px-3 text-slate-600 max-w-[200px] truncate">{item.description}</td>
                                  <td className="py-2 px-3">
                                    <span className="font-mono font-semibold text-[#8B5CF6] bg-[#8B5CF6]/10 px-2 py-0.5 rounded">
                                      {item.suggested_hs_code}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-slate-500">{item.code_description}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Step 2 - Always visible, disabled until improvements exist */}
                  <div className={`rounded-xl border p-4 ${hasImprovements ? 'border-[#6366F1]/30 bg-gradient-to-r from-[#6366F1]/5 to-[#8B5CF6]/5' : 'border-slate-200 bg-slate-50/50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${hasImprovements ? 'bg-[#6366F1] text-white' : 'bg-slate-300 text-white'}`}>2</span>
                      <span className={`text-sm font-semibold ${hasImprovements ? 'text-slate-800' : 'text-slate-400'}`}>
                        Re-classify with improvements
                      </span>
                    </div>

                    {!hasImprovements && (
                      <p className="text-xs text-slate-400 mb-3">Complete Step 1 first — enhance descriptions or suggest HS codes to unlock re-classification.</p>
                    )}
                    {hasImprovements && (
                      <p className="text-xs text-slate-600 mb-3">
                        Apply {aiEnhancements?.enhanced?.length || 0} enhanced descriptions
                        {aiHsSuggestions?.suggestions?.length > 0 && ` and ${aiHsSuggestions.suggestions.length} HS code suggestions`} and run a fresh classification with improved data.
                      </p>
                    )}

                    <Button
                      onClick={handleReclassify}
                      disabled={!hasImprovements || reclassifyLoading}
                      className={hasImprovements
                        ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 shadow-md'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
                      data-testid="reclassify-btn"
                    >
                      {reclassifyLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {reclassifyLoading ? 'Re-classifying...' : 'Re-classify with AI Improvements'}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Next Steps & Export Actions */}
          {results.length > 1 && (
            <div className="grid lg:grid-cols-2 gap-4 mb-8">
              {/* Next Steps Checklist - Collapsible */}
              <div className="card-modern p-5">
                <button 
                  onClick={() => setShowChecklist(!showChecklist)}
                  className="flex items-center justify-between w-full"
                  data-testid="checklist-toggle"
                >
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-[#2563EB]" />
                    <h3 className="font-outfit font-semibold text-slate-900">Next Steps</h3>
                  </div>
                  {showChecklist ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {showChecklist && (
                  <div className="space-y-3 mt-4">
                    <p className="text-xs text-slate-500">Review compliance risks, update codes, and schedule re-classification</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/governance')}>
                        <AlertCircle className="w-3.5 h-3.5 mr-1.5" />Review Risks
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs" onClick={handleExportCSV}>
                        <Download className="w-3.5 h-3.5 mr-1.5" />Export Corrected Codes
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export & Actions */}
              <div className="card-modern p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Download className="w-5 h-5 text-[#6366F1]" />
                  <h3 className="font-outfit font-semibold text-slate-900">Export & Share</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={copyAllCodes}
                    variant="outline"
                    className="justify-start rounded-xl border-slate-200 hover:bg-slate-50"
                    data-testid="copy-all-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" /> Copy All Codes
                  </Button>
                  <Button 
                    onClick={handleExportCSV}
                    variant="outline"
                    className="justify-start rounded-xl border-slate-200 hover:bg-slate-50"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                  {analysis?.type === 'erp_import' && (
                    <Button 
                      onClick={handleDownloadCorrectedFile}
                      className="justify-start col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                      data-testid="download-corrected-btn"
                    >
                      <FileDown className="w-4 h-4 mr-2" /> Download Corrected File
                    </Button>
                  )}
                  <Button 
                    onClick={handleDownloadPdf}
                    className={`justify-start btn-primary-modern ${analysis?.type === 'erp_import' ? '' : 'col-span-2'}`}
                    disabled={pdfLoading}
                    data-testid="download-pdf-btn"
                  >
                    {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                    {pdfLoading ? 'Generating PDF...' : 'Download Full PDF Report'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Single product export actions */}
          {results.length === 1 && (
            <div className="card-modern p-5 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Download className="w-5 h-5 text-[#6366F1]" />
                <h3 className="font-outfit font-semibold text-slate-900">Export & Actions</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={copyAllCodes} variant="outline" className="rounded-xl border-slate-200 hover:bg-slate-50" data-testid="copy-all-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy {analysis?.code_label || 'HS Code'}
                </Button>
                <Button onClick={handleExportCSV} variant="outline" className="rounded-xl border-slate-200 hover:bg-slate-50" data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
                <Button onClick={handleDownloadPdf} className="btn-primary-modern" disabled={pdfLoading} data-testid="download-pdf-btn">
                  {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  {pdfLoading ? 'Generating...' : 'Download PDF'}
                </Button>
                <LandedCostCalculator
                  dutyRate={results[0]?.tariff_current || 0}
                  suggestedDutyRate={results[0]?.tariff_suggested || 0}
                  productCost={results[0]?.unit_cost || 0}
                  originCountry={results[0]?.country_of_origin || ''}
                  destinationCountry={results[0]?.import_country || 'United States'}
                  alternativeOrigins={results[0]?.sourcing_alternatives || null}
                />
              </div>
            </div>
          )}

          {/* ═══════════════════ SECTION 4: DETAILED DATA ═══════════════════ */}

          {/* Full Results Table */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">
                Full Classification Results
              </h3>
              
              {/* Filter Buttons */}
              {results.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 mr-2">Filter:</span>
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === 'all' 
                        ? 'bg-[#2563EB] text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    All ({results.length})
                  </button>
                  <button
                    onClick={() => setStatusFilter('compliance_risk')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === 'compliance_risk' 
                        ? 'bg-[#EF4444] text-white' 
                        : 'bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20'
                    }`}
                  >
                    ⚠️ Compliance Risk ({complianceRiskCount})
                  </button>
                  <button
                    onClick={() => setStatusFilter('savings')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === 'savings' 
                        ? 'bg-[#10B981] text-white' 
                        : 'bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20'
                    }`}
                  >
                    💰 Savings ({savingsItems.length})
                  </button>
                  <button
                    onClick={() => setStatusFilter('correct')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === 'correct' 
                        ? 'bg-slate-600 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    ✓ Correct ({correctItems.length})
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full data-table" data-testid="full-results-table">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">SKU ID</th>
                    <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">Description</th>
                    <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">Current HS</th>
                    <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">Correct HS</th>
                    <th className="text-right py-3 px-4 text-sm text-slate-500 font-medium">Impact</th>
                    <th className="text-center py-3 px-4 text-sm text-slate-500 font-medium">Confidence</th>
                    <th className="text-center py-3 px-4 text-sm text-slate-500 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results
                    .filter(sku => statusFilter === 'all' || getSkuStatus(sku) === statusFilter)
                    .map((sku, index) => {
                      const status = getSkuStatus(sku);
                      return (
                    <React.Fragment key={sku.sku_id}>
                      <tr 
                        className={`border-b border-slate-50 hover:bg-slate-50 ${expandedSku === sku.sku_id ? 'bg-[#F7F9FC]' : ''} ${status === 'compliance_risk' ? 'bg-[#EF4444]/5' : ''}`}
                        data-testid={`sku-row-${sku.sku_id}`}
                      >
                        {/* Status Badge */}
                        <td className="py-3 px-4">
                          {status === 'compliance_risk' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-[#EF4444]/10 text-[#EF4444]">
                              <AlertCircle className="w-3 h-3" />
                              Risk
                            </span>
                          )}
                          {status === 'savings' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-[#10B981]/10 text-[#10B981]">
                              <TrendingUp className="w-3 h-3" />
                              Save
                            </span>
                          )}
                          {status === 'correct' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                              <Check className="w-3 h-3" />
                              OK
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-slate-500">{sku.sku_id}</td>
                        <td className="py-3 px-4 text-sm text-slate-700 max-w-[200px] truncate">
                          {sku.description}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm text-slate-400">
                          {sku.current_hs_code || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-[#10B981] font-medium">{sku.suggested_hs_code}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyToClipboard(sku.suggested_hs_code); }}
                              className="p-1 rounded hover:bg-[#10B981]/10 transition-colors"
                              title={`Copy ${analysis?.code_label || 'HS Code'}`}
                            >
                              {copiedCode === sku.suggested_hs_code ? (
                                <Check className="w-3.5 h-3.5 text-[#10B981]" />
                              ) : (
                                <Copy className="w-3.5 h-3.5 text-slate-400 hover:text-[#10B981]" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-sm">
                          {status === 'compliance_risk' && (
                            <span className="font-semibold text-[#EF4444]">
                              +${Math.abs(sku.estimated_savings || 0).toLocaleString()}
                            </span>
                          )}
                          {status === 'savings' && (
                            <span className="font-semibold text-[#10B981]">
                              -${Math.abs(sku.estimated_savings || 0).toLocaleString()}
                            </span>
                          )}
                          {status === 'correct' && (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            sku.confidence_score >= 0.8 ? 'bg-[#10B981]/10 text-[#10B981]' :
                            sku.confidence_score >= 0.5 ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                            'bg-[#EF4444]/10 text-[#EF4444]'
                          }`}>
                            {Math.round(sku.confidence_score * 100)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleSkuExpand(sku.sku_id)}
                            className="text-[#1E5EFF] hover:bg-[#DCEBFF]/50"
                            data-testid={`expand-sku-${sku.sku_id}`}
                          >
                            {expandedSku === sku.sku_id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <Info className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      {expandedSku === sku.sku_id && (
                        <tr>
                          <td colSpan={8} className="bg-[#F7F9FC] p-6">
                            <div className="space-y-4">
                              {/* Classification Comparison - Show when there's a current HS code */}
                              {sku.current_hs_code && (
                                <div className="bg-white rounded-xl p-4 border border-slate-200">
                                  <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                    <ArrowRightLeft className="w-4 h-4 text-[#2563EB]" />
                                    Classification Comparison
                                  </p>
                                  <div className="grid md:grid-cols-3 gap-4">
                                    {/* Old Classification */}
                                    <div className="bg-[#EF4444]/5 rounded-lg p-3 border border-[#EF4444]/20">
                                      <p className="text-xs font-medium text-[#EF4444] mb-1">CURRENT (Potentially Incorrect)</p>
                                      <p className="font-mono text-lg font-bold text-slate-800">{sku.current_hs_code}</p>
                                      <p className="text-sm text-slate-600 mt-1">Tariff Rate: <span className="font-semibold text-[#EF4444]">{sku.tariff_current}%</span></p>
                                    </div>
                                    
                                    {/* Arrow */}
                                    <div className="flex items-center justify-center">
                                      <div className="flex flex-col items-center">
                                        <ArrowRight className="w-8 h-8 text-[#10B981]" />
                                        {sku.tariff_delta > 0 && (
                                          <span className="text-xs font-bold text-[#10B981] mt-1">
                                            Save {sku.tariff_delta.toFixed(1)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* New Classification */}
                                    <div className="bg-[#10B981]/5 rounded-lg p-3 border border-[#10B981]/20">
                                      <p className="text-xs font-medium text-[#10B981] mb-1">SUGGESTED (Correct Code)</p>
                                      <div className="flex items-center gap-2">
                                        <p className="font-mono text-lg font-bold text-slate-800">{sku.suggested_hs_code}</p>
                                        <button
                                          onClick={() => copyToClipboard(sku.suggested_hs_code)}
                                          className="p-1.5 rounded-md hover:bg-[#10B981]/20 transition-colors group"
                                          title={`Copy ${analysis?.code_label || 'HS Code'}`}
                                        >
                                          {copiedCode === sku.suggested_hs_code ? (
                                            <Check className="w-4 h-4 text-[#10B981]" />
                                          ) : (
                                            <Copy className="w-4 h-4 text-slate-400 group-hover:text-[#10B981]" />
                                          )}
                                        </button>
                                      </div>
                                      <p className="text-sm text-slate-600 mt-1">Tariff Rate: <span className="font-semibold text-[#10B981]">{sku.tariff_suggested}%</span></p>
                                    </div>
                                  </div>
                                  
                                  {/* Value Benefit - Savings scenario */}
                                  {sku.estimated_savings && sku.estimated_savings > 0 && (
                                    <div className="mt-4 p-3 bg-gradient-to-r from-[#10B981]/10 to-[#06B6D4]/10 rounded-lg">
                                      <p className="text-sm text-slate-600">
                                        <span className="font-semibold text-[#10B981]">Estimated Annual Savings:</span>{' '}
                                        <span className="text-xl font-bold text-[#10B981]">${sku.estimated_savings.toLocaleString()}</span>
                                        <span className="text-slate-500 ml-2">(based on provided volume & cost)</span>
                                      </p>
                                    </div>
                                  )}

                                  {/* Compliance Risk - When correct code costs MORE (misclassification for duty evasion) */}
                                  {sku.estimated_savings && sku.estimated_savings < 0 && (
                                    <div className="mt-4 p-3 bg-gradient-to-r from-[#EF4444]/10 to-[#F59E0B]/10 rounded-lg border border-[#EF4444]/30">
                                      <div className="flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                                        <div>
                                          <p className="text-sm font-semibold text-[#EF4444]">⚠️ Compliance Risk Detected</p>
                                          <p className="text-sm text-slate-600 mt-1">
                                            Your current classification may result in <strong>underpayment of duties</strong> by 
                                            <span className="font-bold text-[#EF4444]"> ${Math.abs(sku.estimated_savings).toLocaleString()}/year</span>.
                                          </p>
                                          <p className="text-xs text-slate-500 mt-2">
                                            CBP may assess back duties, penalties, and interest. Recommend consulting with a licensed customs broker.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-slate-500 mb-1">Full Description</p>
                                  <p className="text-slate-700">{sku.description}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-500 mb-1">Alternative Codes</p>
                                  <div className="flex flex-wrap gap-2">
                                    {sku.alternative_codes?.map((alt, i) => (
                                      <span key={i} className="bg-white px-3 py-1 rounded-full text-sm border border-slate-200">
                                        {alt.code} ({alt.tariff_rate}%)
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Expected HS Code and Section 301 indicators */}
                              <div className="flex flex-wrap gap-3 mt-2">
                                {sku.expected_hs_code && (
                                  <div className="inline-flex items-center gap-2 bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-lg px-3 py-2">
                                    <Target className="w-4 h-4 text-[#6366F1]" />
                                    <span className="text-xs text-slate-600">Expected HS:</span>
                                    <span className="font-mono text-sm font-semibold text-[#6366F1]">{sku.expected_hs_code}</span>
                                    {String(sku.current_hs_code).substring(0,6) !== String(sku.expected_hs_code).substring(0,6) && (
                                      <span className="text-xs text-[#EF4444] font-semibold">Mismatch</span>
                                    )}
                                  </div>
                                )}
                                {sku.section_301_applicable && (
                                  <div className="inline-flex items-center gap-2 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg px-3 py-2">
                                    <AlertCircle className="w-4 h-4 text-[#EF4444]" />
                                    <span className="text-xs font-semibold text-[#EF4444]">Section 301 Tariff Applied</span>
                                  </div>
                                )}
                                {sku.country_of_origin && (
                                  <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                    <span className="text-xs text-slate-500">Origin:</span>
                                    <span className="text-xs font-semibold text-slate-700">{sku.country_of_origin}</span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-500 mb-2">AI Reasoning</p>
                                {loadingReasoning[sku.sku_id] ? (
                                  <div className="flex items-center gap-2 text-slate-500">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Loading detailed reasoning...
                                  </div>
                                ) : (
                                  <div className="bg-white rounded-lg p-4 border border-slate-200 text-slate-600 whitespace-pre-wrap">
                                    {skuReasoning[sku.sku_id] || sku.reasoning || 'Click to load detailed reasoning...'}
                                  </div>
                                )}
                              </div>
                              
                              {/* Landed Cost Calculator for this SKU */}
                              <div className="mt-4 pt-4 border-t border-slate-200">
                                <LandedCostCalculator
                                  dutyRate={sku.tariff_current || 0}
                                  suggestedDutyRate={sku.tariff_suggested || 0}
                                  productCost={sku.unit_cost || 0}
                                  originCountry={sku.country_of_origin || ''}
                                  destinationCountry={sku.import_country || 'United States'}
                                  trigger={
                                    <Button
                                      variant="outline"
                                      className="w-full rounded-xl border-[#059669]/30 text-[#059669] hover:bg-[#059669]/5"
                                      data-testid={`landed-cost-btn-${sku.sku_id}`}
                                    >
                                      <Calculator className="w-4 h-4 mr-2" />
                                      Calculate Landed Cost for this SKU
                                    </Button>
                                  }
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="mt-6 text-center text-sm text-slate-400">
            Optimization suggestions based on AI analysis. Not legal advice.
          </p>

          {/* Team Comments Section */}
          {isSharedWithTeam && (
            <div className="mt-8">
              <AnalysisComments analysisId={analysisId} currentUser={user} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AnalysisResults;
